import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Store, Wallet, BadgeCheck, CalendarDays } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import ProductCard, { Stars } from "@/components/ProductCard";

export default function VendorStorefrontPage() {
  const { sellerId } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/vendors/${sellerId}`).then(({ data }) => setData(data)).catch(() => setNotFound(true));
  }, [sellerId]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-8 mt-20 text-center" data-testid="store-not-found">
        <p className="font-heading text-2xl font-bold">Store not found</p>
        <Link to="/" className="text-blue-600 text-sm font-semibold mt-4 inline-block">← Back to marketplace</Link>
      </div>
    );
  }
  if (!data) return <div className="max-w-7xl mx-auto px-8 mt-12"><div className="h-96 rounded-2xl bg-slate-100 animate-pulse" /></div>;

  const { seller, products, posts } = data;
  const pay = seller.payment_config || {};
  const payConfigured = pay.upi_id || pay.account_number;

  return (
    <div data-testid="vendor-store-page" className="max-w-7xl mx-auto px-4 sm:px-8 mt-8">
      <div className="rounded-2xl bg-slate-900 text-white p-8 sm:p-12">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold flex items-center gap-2"><Store className="w-3.5 h-3.5" /> Vendor store</p>
            <h1 className="font-heading text-3xl sm:text-5xl font-bold tracking-tight mt-2">{seller.business_name}</h1>
            <div className="flex items-center gap-4 mt-4 text-sm text-slate-300 flex-wrap">
              <span className="inline-flex items-center gap-1.5"><Stars rating={seller.rating} /> {seller.rating}</span>
              <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> Selling since {new Date(seller.created_at).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}</span>
              {(seller.categories || []).map((c) => <span key={c} className="text-[10px] uppercase tracking-widest font-bold bg-white/10 px-2.5 py-1 rounded-full">{c}</span>)}
            </div>
          </div>
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-300 bg-emerald-400/10 px-3 py-1.5 rounded-full">
            <BadgeCheck className="w-4 h-4" /> Admitted vendor
          </span>
        </div>
        {payConfigured && (
          <div data-testid="store-payment-info" className="mt-8 rounded-xl bg-white/5 border border-white/10 p-5 text-sm max-w-xl">
            <p className="font-bold flex items-center gap-2 text-white"><Wallet className="w-4 h-4" /> Pay this vendor directly</p>
            <div className="mt-2 space-y-1 text-slate-300 text-xs">
              {pay.upi_id && <p>UPI: <span className="font-mono font-semibold text-white">{pay.upi_id}</span></p>}
              {pay.account_number && <p>Bank: {pay.account_name} · A/C <span className="font-mono">{pay.account_number}</span> · IFSC <span className="font-mono">{pay.ifsc}</span> · {pay.bank_name}</p>}
              {pay.note && <p className="text-slate-400">{pay.note}</p>}
            </div>
          </div>
        )}
      </div>

      {posts.length > 0 && (
        <section data-testid="store-posts" className="mt-12">
          <p className="text-xs uppercase tracking-widest text-blue-500 font-bold">Announcements</p>
          <h2 className="font-heading text-2xl font-bold tracking-tight mt-1 mb-6">From {seller.business_name}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {posts.map((p) => (
              <div key={p.id} data-testid={`store-post-card-${p.id}`} className="border border-slate-100 rounded-xl overflow-hidden">
                {p.image_url && <img src={fileUrl(p.image_url)} alt="" className="w-full h-40 object-cover" />}
                <div className="p-5">
                  <p className="font-semibold">{p.title}</p>
                  <p className="text-sm text-slate-500 mt-1.5 whitespace-pre-wrap">{p.body}</p>
                  <p className="text-xs text-slate-400 mt-3">{new Date(p.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section data-testid="store-products" className="mt-12">
        <p className="text-xs uppercase tracking-widest text-blue-500 font-bold">Catalogue</p>
        <h2 className="font-heading text-2xl font-bold tracking-tight mt-1 mb-6">Products from {seller.business_name}</h2>
        {products.length ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        ) : (
          <p className="text-sm text-slate-400 py-10 text-center rounded-xl bg-slate-50">No products in stock right now.</p>
        )}
      </section>
    </div>
  );
}
